import { gql } from '@apollo/client';

// ─── Shared Types ─────────────────────────────────────────────────────────────

export interface EmployeeService {
  id: string;
  name: string;
  price: string; // response type — API returns as string; convert to Number when used as input
  duration: string; // response type — API returns as string; convert to Int when used as input
}

export interface EmployeeAvailability {
  day: string;
  available: boolean;
  startTime: string;
  endTime: string;
}

export interface EmployeeProfile {
  id: string;
  ownerId: string;
  firstName: string;
  lastName: string;
  profilePic?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  email: string;
  countryCode: string;
  phone: string;
  hireDate?: string | null;
  yearsOfExperience?: number | null;
  bio?: string | null;
  status: string;
  languages: string[];
  specialties: string[];
  services: EmployeeService[];
  availability: EmployeeAvailability[];
  isVerified: boolean;
  isActive: boolean;
  isPasswordTemporary: boolean;
  isVirtualOwner?: boolean; // owner acting as barber — do not show in Your Team list
  createdAt: string;
  updatedAt: string;
}

// ─── Get All Employees ────────────────────────────────────────────────────────

export const GET_EMPLOYEES_QUERY = gql`
  query GetEmployees($filter: GetEmployeesFilter) {
    getEmployees(filter: $filter) {
      employees {
        id
        ownerId
        firstName
        lastName
        profilePic
        dateOfBirth
        gender
        email
        countryCode
        phone
        hireDate
        yearsOfExperience
        bio
        status
        languages
        specialties
        services {
          id
          name
          price
          duration
        }
        availability {
          day
          available
          startTime
          endTime
        }
        isVerified
        isActive
        isPasswordTemporary
        isVirtualOwner
        createdAt
        updatedAt
      }
      total
    }
  }
`;

export interface GetEmployeesFilter {
  status?: string;
  search?: string;
}

export interface GetEmployeesResponse {
  getEmployees: {
    employees: EmployeeProfile[];
    total: number;
  };
}

// ─── Get Single Employee ──────────────────────────────────────────────────────

export const GET_EMPLOYEE_QUERY = gql`
  query GetEmployee($employeeId: ID!) {
    getEmployee(employeeId: $employeeId) {
      id
      ownerId
      firstName
      lastName
      profilePic
      dateOfBirth
      gender
      email
      countryCode
      phone
      hireDate
      yearsOfExperience
      bio
      status
      languages
      specialties
      services {
        id
        name
        price
        duration
      }
      availability {
        day
        available
        startTime
        endTime
      }
      isVerified
      isActive
      isPasswordTemporary
      isVirtualOwner
      createdAt
      updatedAt
    }
  }
`;

export interface GetEmployeeResponse {
  getEmployee: EmployeeProfile;
}

// ─── Update Employee ──────────────────────────────────────────────────────────

export const UPDATE_EMPLOYEE_MUTATION = gql`
  mutation UpdateEmployee($input: UpdateEmployeeInput!) {
    updateEmployee(input: $input) {
      id
      ownerId
      firstName
      lastName
      profilePic
      dateOfBirth
      gender
      email
      countryCode
      phone
      hireDate
      yearsOfExperience
      bio
      status
      languages
      specialties
      services {
        id
        name
        price
        duration
      }
      availability {
        day
        available
        startTime
        endTime
      }
      isVerified
      isActive
      isPasswordTemporary
      isVirtualOwner
      createdAt
      updatedAt
    }
  }
`;

export interface UpdateEmployeeServiceInput {
  serviceId: string;
  price: number; // updateEmployee mutation expects Float! — do NOT send as string
  duration: number; // updateEmployee mutation expects Int! — do NOT send as string
}

export interface UpdateEmployeeAvailabilityInput {
  day: string;
  available: boolean;
  startTime: string;
  endTime: string;
}

export interface UpdateEmployeeInput {
  employeeId: string;
  firstName?: string;
  lastName?: string;
  profilePic?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  countryCode?: string;
  phone?: string;
  hireDate?: string | null;
  yearsOfExperience?: number;
  bio?: string | null;
  status?: string;
  languageIds?: string[];
  specialtyIds?: string[];
  services?: UpdateEmployeeServiceInput[];
  availability?: UpdateEmployeeAvailabilityInput[];
}

export interface UpdateEmployeeResponse {
  updateEmployee: EmployeeProfile;
}

// ─── Delete Employee ──────────────────────────────────────────────────────────

export const DELETE_EMPLOYEE_MUTATION = gql`
  mutation DeleteEmployee($employeeId: ID!) {
    deleteEmployee(employeeId: $employeeId) {
      success
      message
      deletedEmployeeId
    }
  }
`;

export interface DeleteEmployeeResponse {
  deleteEmployee: {
    success: boolean;
    message: string;
    deletedEmployeeId: string;
  };
}
